import _ from 'lodash'
import moment from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import { Form } from 'semantic-ui-react'
import { endpoint } from '../../../api/http'
import { GetCancelReservations, GetReservationTotals } from '../../../api/services/pms'
import { useCurrentHotel } from '../../../hooks/useCurrentHotel'
import { useTextResources } from '../../../language'
import GridSearchTemplate from '../../../pages/GridSearchTemplate'
import * as reservationPolicyUtils from '../../shared/reservationPolicy'
import CheckboxWrapper from '../../shared/Checkbox'
import { dateFormat, dateTimeFormat } from '../../shared/constants'
import { Currencies } from '../../shared/currencies'
import { DatePicker } from '../../shared/DatePicker'
import { createExcelHeader } from '../../GridPanel/utility'
import DropDown from '../../shared/dropdown'
import HotelsDropDown from '../../shared/HotelsDropDown'
import { ReservationStatusesEnums } from '../../shared/statustypes'
const CancelReservationsReport = () => {
    const text = useTextResources()
    const currentHotel = useCurrentHotel()

    const [data, setData] = useState([])
    const [totals, setTotals] = useState([])

    const [dates, setDates] = useState([new Date(), new Date()])
    const [cancelByDateType, setCancelByDateType] = useState('CancelDate');
    const [includeNoShow, setIncludeNoShow] = useState(true);

    const reservationFormatter = useCallback(({ value, row }) => {
        console.log('reservationFormatter start');
        if (row) {
            return (
                <a target="_blank" rel="noopener noreferrer" href={`${endpoint}PMS/Reservations/Index/${row.ReservationId}?hotelid=${row.HotelId}`}>
                    {value}
                </a>
            )
        } else {
            return value
        }
    }, [])

    /* eslint-enable react/prop-types */
    const getDateCellValue = useCallback((row, columnName) => {
        console.log('getDateCellValue start');
        const date = row[columnName];
        return date && moment(date).format(dateFormat);
    }, [])

    const getDateTimeCellValue = useCallback((row, columnName) => {
        console.log('getDateTimeCellValue start');
        const date = row[columnName];
        return date && moment(date).format(dateTimeFormat);
    }, [])

    const getBooleanCellValue = useCallback((row, columnName) => {
        const value = row[columnName];
        if (_.isNil(value)) {
            return null;
        }
        return value ? text("Yes") : text("No");
    }, [text])

    const pastCancellationDeadlineFormatter = useCallback(({ value, row }) => {
        const rawValue = row?.PastCancellationDeadline;
        if (_.isNil(rawValue)) {
            return null;
        }
        return (
            <span style={{
                color: rawValue ? "red" : undefined
            }}>
                {value}
            </span>
        )
    }, [])

    const currencyFormatter = useCallback(({ value }) =>
        value && `${value.toFixed(2)} ${getCurrencyByCurrentHotel()}`,
        [])

    const decimalFormatter = useCallback(({ value }) =>
        value?.toFixed(2),
        [])

    const getFormatedDate = useCallback((cancelType, index) =>
        dates &&
            cancelByDateType === cancelType ?
            moment(dates[index]).toISOString() :
            undefined,
        [dates, cancelByDateType])

    const getDisplayFormatedDate = useCallback((index) => dates &&
    moment(dates[index]).format(dateFormat), [dates])

    const getCurrencyByCurrentHotel = useCallback(() => {
        const currencyCode = currentHotel?.MainCurrencyId;
        return Currencies[currencyCode]?.symbol ?? '';
    }, [currentHotel?.MainCurrencyId])

    const excelHeaderTitle = `${text("CancelAndNoShowReservation")} ${getDisplayFormatedDate(0)} - ${getDisplayFormatedDate(1)}`;

    const reservationStatus = useMemo(() => {
        const status = [ReservationStatusesEnums.Cancelled, ReservationStatusesEnums.Deleted];

        if (includeNoShow) {
            status.push(ReservationStatusesEnums.NoShow)
        }

        return status
    }, [includeNoShow])

    const cancelByDateTypeData = useMemo(() => [
        {
            text: text('CancelOrNoShowDate'),
            value: 'CancelDate'
        },
        {
            text: text('CheckInDate'),
            value: 'CheckInDate'
        }
    ], [])

    const summaryItems = useMemo(() => [
        { columnName: 'ReservationNumber', type: 'count' },
        { columnName: 'Nights', type: 'avg' },
        { columnName: 'Nights', type: 'sum' },
        { columnName: 'ProductIncome', type: 'sum' },
        { columnName: 'ExtraAddonIncome', type: 'sum' },
        { columnName: 'TotalIncomeByHotelMainCurrency', type: 'sum' },
    ], [])

    const getReservationTotals = async (reservations) => {
        const reservatinIds = reservations.map(r => r.ReservationId)
        const hotelIds = reservations.map(r => r.HotelId);

        const totals = await GetReservationTotals({
            ReservationIDs: reservatinIds,
            HotelIds: hotelIds,
            TargetCurrencyID: currentHotel.MainCurrencyId
        })

        setTotals(totals)
    }

    const onSearchComplete = useCallback(async (data) => {
        if (!data?.length) {
            return;
        }
        await getReservationTotals(data)
        setData(data)
    }, [])

    const gridData = useMemo(() => {
        if (!data?.length) {
            return []
        }

        return data.map(d => {
            if (!d.ReservationPolicy) {
                return d;
            }

            const cancellationDeadline = reservationPolicyUtils.getFreeCancellationDate(d, d.ReservationPolicy?.CancellationPolicies);
            const pastCancellationDeadline = cancellationDeadline ? moment().isAfter(cancellationDeadline) : undefined
            const total = totals.find(t => t.ReservationID === d.ReservationId);
            const cancellationPenalty = reservationPolicyUtils.getUpcomingCancellationFee(d.ReservationPolicy?.CancellationPolicies, d, total)
            return ({
                ...d,
                CancellationDeadline: cancellationDeadline,
                PastCancellationDeadline: pastCancellationDeadline,
                ReservationPolicyName: d.ReservationPolicy?.Name,
                CancellationPenalty: cancellationPenalty
            })
        })
    }, [data, totals])

    const gridOptions = {
        search: {
            title: text("CancelAndNoShowReservation"),
            fields: [
                <Form.Field
                    label={text("Hotel")}
                    control={HotelsDropDown}
                    key="HotelIDs" clearable width="4"
                    allowSelectAll multiple />,
                <Form.Field
                    label={text("CancelByDateType")}
                    control={DropDown}
                    key="CancelByDateType"
                    selectFirst
                    textField="text"
                    valueField="value"
                    data={cancelByDateTypeData}
                    value={cancelByDateType}
                    onChange={(value) => {
                        if (!_.isEqual(cancelByDateType, value)) {
                            setCancelByDateType(value)
                        }
                    }}
                />,
                <DatePicker
                    label={text("CancelDate")}
                    picktype='range'
                    key="CancelDateRange"
                    value={dates}
                    onChange={setDates} />,
                <Form.Input
                    key="IncludeNoShow"
                    control={CheckboxWrapper}
                    label={text("IncludeNoShow")}
                    value={includeNoShow}
                    onChange={setIncludeNoShow}
                />
            ],
            initial: {
                IncludeNoShow: true,
            },
            api: GetCancelReservations,
            spec: {
                FromCancelDate: getFormatedDate('CancelDate', 0),
                ToCancelDate: getFormatedDate('CancelDate', 1),
                FromCheckInDate: getFormatedDate('CheckInDate', 0),
                ToCheckInDate: getFormatedDate('CheckInDate', 1),
                ReservationStatuses: reservationStatus,
                IncludeCanceled: true,
                IncludeAddons: true,
                IncludeTotalAmounts: true,
                IncludeReservationCompanyAgent: true,
                IncludeRoom: true,
                IncludeReservaionCustomerCommission: true,
                IncludeReservationCancellationReason: true,
                IncludeReservationDailyAccommodation: true,
                IncludeRoomType: true,
                IncludeReservationPolicy: true,
                IncludeCancellationPolicy: true,
            },
            groupSize: 6
        },
        grid: {
            columns: [
                { name: 'HotelName', title: text("HotelName") },
                { name: 'ReservationNumber', title: 'Reservation', formatter: reservationFormatter },
                { name: 'CheckInDate', title: text("CheckInDate"), getCellValue: getDateCellValue },
                { name: 'CheckOutDate', title: text("CheckOutDate"), getCellValue: getDateCellValue },
                { name: 'Nights', title: text("Nights"), formatter: decimalFormatter },
                { name: 'CreationDate', title: text("CreateDate"), getCellValue: getDateCellValue  },
                { name: 'ReservationCancelDayTime', title: text("CancelDayTime"), getCellValue: getDateTimeCellValue },


                { name: 'CancellationDeadline', title: text("CancellationDeadline"), getCellValue: getDateTimeCellValue },
                { name: 'PastCancellationDeadline', title: text("PastCancellationDeadline"), getCellValue: getBooleanCellValue, formatter: pastCancellationDeadlineFormatter },
                { name: 'ReservationPolicyName', title: text("ReservationPolicy") },
                { name: 'CancellationPenalty', title: text("CancellationPenalty") },

                { name: 'RoomTypeName', title: text("RoomType") },
                { name: 'ProductIncome', title: text("ProductIncome"), formatter: currencyFormatter },
                { name: 'ExtraAddonIncome', title: text("ExtraAddonIncome"), formatter: currencyFormatter },
                { name: 'TotalIncomeByHotelMainCurrency', title: text("TotalIncome"), formatter: currencyFormatter },
                { name: 'AgentName', title: text("AgentName") },
                { name: 'CompanyName', title: text("CompanyName") },
                { name: 'CancelationReason', title: text("CancellationReasonDescripiton") },
                { name: 'CancelDescription', title: text("CancelDescription") },
                { name: 'CancelUserName', title: text("CancelUserName") },
            ],
            summary: true,
            totalSummaryItems: summaryItems,
            export: true,
            excel: {
                customizeHeader: createExcelHeader(excelHeaderTitle),
                totalSummaryItems: summaryItems,
            },
            scrollbarX: true
        },
    }


    return (
        <GridSearchTemplate options={gridOptions} onSearchComplete={onSearchComplete} data={gridData} />
    )
}
export default CancelReservationsReport
